import React from 'react';
import { Row, Col, Card, Collapse } from 'react-bootstrap';

import { render_marked_text } from '../utils/HiddenText';

function StructuralAnalysis({
	analysis_results,
	show_structural,
	set_show_structural
}){

	// The Structured data group's rows, each { heading, lines: [{ text, is_warning }] }: unreadable JSON-LD, then
	// each item with what it lacks, then each place it disagrees with the page, then FAQ questions not on the page,
	// then the subjects it names that only the page's hidden text introduces (scoring.structured_data_hidden_subjects).
	// The PDF report builds the same rows (AEOReportCreator.get_structured_data_rows).
	const get_structured_data_rows = (data)=>{
		const hidden_subjects = (analysis_results.scoring && Array.isArray(analysis_results.scoring.structured_data_hidden_subjects)) ?
			analysis_results.scoring.structured_data_hidden_subjects :
			[];
		const structured_data = data.structured_data ? data.structured_data : {};
		const items = Array.isArray(structured_data.items) ? structured_data.items : [];
		const checks = Array.isArray(structured_data.checks) ? structured_data.checks : [];
		const faq_questions = Array.isArray(structured_data.faq_questions) ? structured_data.faq_questions : [];
		const block_count = Number(structured_data.json_ld_block_count) || 0;
		const invalid_block_count = Number(structured_data.invalid_json_ld_block_count) || 0;
		const rows = [];

		if((items.length === 0) && (block_count === 0)){
			rows.push({ heading: 'None found', lines: [] });
			return rows;
		}

		if(invalid_block_count > 0){
			rows.push({
				heading: 'Unreadable JSON-LD',
				lines: [{ text: invalid_block_count + ' of ' + block_count + ' blocks are not valid JSON.', is_warning: true }]
			});
		}

		items.forEach((item)=>{
			const lines = [];
			const missing_required = Array.isArray(item.missing_required) ? item.missing_required : [];
			const missing_recommended = Array.isArray(item.missing_recommended) ? item.missing_recommended : [];
			if(missing_required.length > 0){
				lines.push({ text: 'Missing required: ' + missing_required.join(', '), is_warning: true });
			}

			if(missing_recommended.length > 0){
				lines.push({ text: 'Missing recommended: ' + missing_recommended.join(', '), is_warning: false });
			}

			if(item.has_rules && (missing_required.length === 0) && (missing_recommended.length === 0)){
				lines.push({ text: 'All checked properties present', is_warning: false });
			}

			rows.push({ heading: item.types.join(', ') + ' (' + item.format + ')', lines: lines });
		});

		checks.forEach((check)=>{
			if(!check.passed){
				rows.push({ heading: 'Does not match the page', lines: [{ text: check.description, is_warning: true }] });
			}
		});

		const unmatched_questions = faq_questions.filter((faq_question)=>{
			return !(faq_question.question_on_page && faq_question.answer_on_page);
		});
		if(unmatched_questions.length > 0){
			rows.push({
				heading: 'FAQ questions not on the page',
				lines: unmatched_questions.map((faq_question)=>{
					return { text: faq_question.question ? faq_question.question : '(no question text)', is_warning: true };
				})
			});
		}

		if(hidden_subjects.length > 0){
			rows.push({
				heading: 'Names subjects a visitor cannot see',
				lines: hidden_subjects.map((subject)=>{
					return { text: String(subject), is_warning: true };
				})
			});
		}

		return rows;
	};

	const render_structured_data_line = (line, line_index)=>{
		return (
			<div
				key={line_index}
				className={"structured-data-line" + (line.is_warning ? " structured-data-warning" : "")}>
				{render_marked_text(line.text, analysis_results)}
			</div>
		);
	};

	const render_structured_data_row = (row, row_index)=>{
		return (
			<div key={row_index} className="structural-text-box structured-data-box">
				<div className="structured-data-heading">{row.heading}</div>
				{row.lines.map(render_structured_data_line)}
			</div>
		);
	};

	const render_structured_data = (data)=>{
		return (
			<div className="structural-section-group">
				<div className="structural-section-heading">Structured data</div>
				{get_structured_data_rows(data).map(render_structured_data_row)}
			</div>
		);
	};

	const render_structural_analysis_section = ()=>{
		const data = analysis_results.structural_analysis;
		if(analysis_results.structural_analysis){
			return (
				<Card className="analysis-results-card structural-analysis-card">
					<h3 className="structural-analysis-title" 
						onClick={()=>set_show_structural(!show_structural)} 
						style={{cursor:'pointer'}}>
						Structural Analysis {show_structural ? "▲" : "▼"}
					</h3>
					<p className="structural-analysis-desc">
						How well the page uses headings, schema, links, and image attributes to support SEO and content structure.
					</p>

					<Collapse in={show_structural}>
					<div>
					<Row>
						<Col md={4}>
							<div className="structural-bool-list">

								<div className={
									"structural-bool-item " +
									(data.has_h1 ? "structural-bool-true" : "structural-bool-false")
								}>
									<span className="structural-bool-icon"></span>
									<span className="structural-bool-label">Has H1 tag</span>
								</div>

								<div className={
									"structural-bool-item " +
									(data.meta_description ? "structural-bool-true" : "structural-bool-false")
								}>
									<span className="structural-bool-icon"></span>
									<span className="structural-bool-label">Meta description present</span>
								</div>

								<div className={
									"structural-bool-item " +
									(data.has_canonical ? "structural-bool-true" : "structural-bool-false")
								}>
									<span className="structural-bool-icon"></span>
									<span className="structural-bool-label">Canonical link present</span>
								</div>

								<div className={
									"structural-bool-item " +
									(data.lang ? "structural-bool-true" : "structural-bool-false")
								}>
									<span className="structural-bool-icon"></span>
									<span className="structural-bool-label">Page language declared{data.lang ? " (" + data.lang + ")" : ""}</span>
								</div>

								<div className="structural-count-box">
									<div className="structural-count-label">Word count</div>
									<div className="structural-count-value">{data.word_count}</div>
								</div>

								<div className="structural-count-box">
									<div className="structural-count-label">Internal links</div>
									<div className="structural-count-value">{data.internal_links}</div>
								</div>

								<div className={
									"structural-count-box" +
									((Number(data.external_links) === 0) ? " structural-count-warning" : "")
								}>
									<div className="structural-count-label">External links</div>
									<div className="structural-count-value">{data.external_links}</div>
								</div>

							</div>
						</Col>

						<Col md={8}>

							<div className="structural-section-group">
								<div className="structural-section-heading">Heading hierarchy</div>
								{data.heading_hierarchy.map((h, index)=>(
									<div key={index} className="structural-text-box">{h}</div>
								))}
							</div>

							<div className="structural-section-group">
								<div className="structural-section-heading">Image alt text</div>
								<div className="structural-image-stats">
									<div className="structural-image-stat">With alt: {data.images_with_alt}</div>
									{(Number(data.images_without_alt) > 0) && (
										<div className="structural-image-stat structural-image-warning">Without alt: {data.images_without_alt}</div>
									)}
								</div>
							</div>

							<div className="structural-section-group">
								<div className="structural-section-heading">Head markup</div>
								<div className="structural-count-box">
									<div className="structural-count-label">Title tag ({data.title ? data.title.length : 0} characters)</div>
									<div className="structural-count-value structural-count-text">{data.title ? data.title : "None"}</div>
								</div>
								<div className="structural-count-box">
									<div className="structural-count-label">Meta description ({data.meta_description ? data.meta_description.length : 0} characters)</div>
									<div className="structural-count-value structural-count-text">{data.meta_description ? data.meta_description : "None"}</div>
								</div>
							</div>

							{render_structured_data(data)}

						</Col>
					</Row>
					</div>
					</Collapse>
				</Card>
			);
		}
	};

	if(!analysis_results || !analysis_results.structural_analysis){
		return null;
	}

	return render_structural_analysis_section();
}

export default StructuralAnalysis;
